import { useEffect, useState } from "react";
import classNames from "classnames/bind";
import styles from "./MediaMedium.module.scss";
import { buildContents } from "./mediaHelper";
import {
  GalleryObject,
  GalleryObjectType,
  VideoPreloadValue,
} from "../../utils/utils.ts";

import { GalleryContent } from "./GalleryContent";
import ResponsiveImage from "../ImageMagnifier/ResponsiveImage";
import { useStandardProps } from "@/context/StandardPropsContext/StandardPropsContext";

const cx = classNames.bind(styles);

interface MediaMediumProps {
  galleryObjects: Array<GalleryObject>;
  openModal: (event: any, imageSrc: string) => void; // eslint-disable-line @typescript-eslint/no-explicit-any
  usePrefetching?: boolean;
  limitImagesOnGrid?: number;
  disableFullWidthImageOnGrid?: boolean;
  hideVideoModal?: boolean;
  videoPreload: VideoPreloadValue;
  carouselGalleryInpTest?: boolean;
  galleryImageZoomOnHover?: boolean;
  ref?: React.RefObject<HTMLDivElement | null>;
}

const MediaMedium = ({
  galleryObjects,
  openModal,
  usePrefetching,
  limitImagesOnGrid,
  disableFullWidthImageOnGrid,
  videoPreload,
  carouselGalleryInpTest,
  galleryImageZoomOnHover,
  ref,
}: MediaMediumProps) => {
  const [zoomTooltipPortalContainer, setZoomTooltipPortalContainer] =
    useState<HTMLDivElement>();

  const { flags } = useStandardProps();

  const gridGalleryVideoLastPosition =
    flags.gridGalleryVideoLastPosition?.value === true;

  useEffect(() => {
    const container = document.createElement("div");
    setZoomTooltipPortalContainer(container);
    document.body.appendChild(container);

    return () => {
      document.body.removeChild(container);
    };
  }, []);

  const videos = galleryObjects.filter(
    (go) => go.type === GalleryObjectType.VIDEO,
  );
  const images = galleryObjects.filter(
    (go) => go.type === GalleryObjectType.IMAGE,
  );

  const contents = buildContents(images, videos, {
    limitItemsOnGrid: limitImagesOnGrid,
    hideVideosOnGrid: false,
    includeRemainingVideos: true,
    ...(gridGalleryVideoLastPosition ? { videoStartAt: images.length } : {}),
  });

  const contentsLength = contents.length;

  const isContentEmpty = contentsLength === 0;
  const isFullWidthContent =
    contentsLength === 1 && !disableFullWidthImageOnGrid;

  let topContents: Array<GalleryObject> = [];
  let bottomContents: Array<GalleryObject> = [];

  if (contentsLength < 3) {
    topContents = contents;
  } else if (contentsLength < 4) {
    topContents = contents.slice(0, 2);
    bottomContents = contents.slice(2);
  } else {
    if (contentsLength % 2 === 0) {
      topContents = contents;
    } else {
      topContents = contents.slice(0, contentsLength - 3);
      bottomContents = contents.slice(contentsLength - 3);
    }
  }

  if (!gridGalleryVideoLastPosition) {
    bottomContents.sort((a, b) => {
      if (a.type < b.type) {
        return 1;
      }
      if (a.type > b.type) {
        return -1;
      }
      return 0;
    });
  }

  const getAltText = (title: string, index: number) =>
    `Product image ${index} of ${contentsLength}, which shows ${title}`;

  return (
    <div>
      <div className={styles.productImagesContainer} data-cy="product-images">
        <div>
          {!isContentEmpty && (
            <div>
              <div
                className={cx({
                  even: !isFullWidthContent && topContents.length % 2 === 0,
                  fullWidthImage: isFullWidthContent,
                  halfWidthImage:
                    disableFullWidthImageOnGrid && topContents.length === 1,
                })}
                data-testid={"grid:gallery:top"}
              >
                {topContents?.map((content, index) => (
                  <GalleryContent
                    content={content}
                    openModal={openModal}
                    key={"top-" + index}
                    zoomTooltipPortalContainer={zoomTooltipPortalContainer}
                    gridIndex={index}
                    altText={getAltText(content.title, index + 1)}
                    usePrefetching={usePrefetching}
                    videoPreload={videoPreload}
                    carouselGalleryInpTest={carouselGalleryInpTest}
                    galleryImageZoomOnHover={galleryImageZoomOnHover}
                    ref={ref}
                  />
                ))}
              </div>
              <div
                className={cx({
                  even:
                    bottomContents.length === 1 ||
                    bottomContents.length % 2 === 0,
                  odd:
                    bottomContents.length !== 1 &&
                    bottomContents.length % 2 !== 0,
                })}
                data-testid={"grid:gallery:bottom"}
              >
                {bottomContents?.map((content, index) => (
                  <GalleryContent
                    content={content}
                    openModal={openModal}
                    key={"bottom-" + index}
                    position={bottomContents.length % 2 === 0 ? "even" : "odd"}
                    zoomTooltipPortalContainer={zoomTooltipPortalContainer}
                    gridIndex={index + topContents.length}
                    altText={getAltText(
                      content.title,
                      index + topContents.length + 1,
                    )}
                    videoPreload={videoPreload}
                    carouselGalleryInpTest={carouselGalleryInpTest}
                    galleryImageZoomOnHover={galleryImageZoomOnHover}
                  />
                ))}
              </div>
            </div>
          )}

          {isContentEmpty && (
            <div
              className={styles.singleImage}
              data-testid={"grid:gallery:empty"}
            >
              {isContentEmpty && (
                <ResponsiveImage
                  src={"//media.johnlewiscontent.com/i/JohnLewis/xxx"}
                  alt="image unavailable"
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MediaMedium;
